import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/format';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { checked, onChange, label, description, disabled, className, id, ...rest },
  ref,
) {
  const checkboxId = id || `checkbox-${Math.random().toString(36).slice(2, 9)}`;
  return (
    <label
      htmlFor={checkboxId}
      className={cn(
        'flex items-start gap-3 p-3 rounded border border-line cursor-pointer transition-colors hover:bg-canvas/50',
        checked && 'border-primary bg-primary/5',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      <div className="flex-shrink-0 mt-0.5">
        <input
          ref={ref}
          id={checkboxId}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className="sr-only peer"
          {...rest}
        />
        <div
          className={cn(
            'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
            checked ? 'bg-primary border-primary' : 'border-line bg-surface',
          )}
        >
          {checked && (
            <svg
              className="w-3 h-3 text-white"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M2 6 L5 9 L10 3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      </div>
      {(label || description) && (
        <div className="flex-1 min-w-0">
          {label && <p className="text-body font-medium text-ink">{label}</p>}
          {description && (
            <p className="text-caption text-ink-subtle mt-0.5">{description}</p>
          )}
        </div>
      )}
    </label>
  );
});
